import React, { useRef, useEffect, FC, UIEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Box,
  Typography,
  Chip,
  Badge,
} from '@mui/material';

// --- Mock 데이터 및 훅 (테스트 및 실행을 위한 가상 구현) ---
// 실제 프로젝트에서는 이 부분을 삭제하고 실제 경로에서 import 하세요.
const useResponsive = () => ({ isMobile: window.innerWidth < 600 });
const AccountSwitcher = () => (
  <Box sx={{ p: 2, background: 'rgba(0,0,0,0.05)', borderRadius: 2, textAlign: 'center' }}>
    <Typography variant="subtitle2">가계부 전환기</Typography>
  </Box>
);
// --- 여기까지 Mock 데이터 ---

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  onMenuClick?: (path: string) => void; // 메뉴 클릭 핸들러 추가
  /** 현재 보고 있는 메뉴의 path — 활성 항목 하이라이트용 */
  activePath?: string;
}

// 메뉴 아이템 타입 정의
interface MenuItem {
  title: string;
  path: string;
  description: string;
  badge?: string;
}

// 메뉴 항목 데이터 — 텍스트 라벨 (40+ 가독성 유지)
const menuItems: MenuItem[] = [
  { title: '대시보드', path: '/', description: '전체 현황 및 요약' },
  { title: '가계부', path: '/accounts', description: '거래 내역 관리' },
  { title: 'AI 세무 보조', path: '/ledger-tax', description: '간편장부 초안 · 엑셀 · 세무사 자료', badge: 'AI' },
  { title: '할일 메모', path: '/memo-wall', description: '포스트잇 할일 메모' },
  { title: '반복거래', path: '/repeats', description: '반복 거래 내역 관리' },
  { title: '인사관리', path: '/employee-management', description: '직원 관리 및 급여 계산' },
  { title: '거래처 관리', path: '/merchants', description: '거래처 명부 관리' },
  { title: '문서관리', path: '/documents', description: '문서 작성 및 관리' },
  { title: '전체 매장 현황', path: '/all-stores', description: '모든 매장의 매출, 지출, 직원, 인건비 종합 현황' },
];

const subMenuItems: MenuItem[] = [
  { title: '매출 분석', path: '/analytics/revenue', description: '매출 트렌드 분석' },
  { title: '지출 분석', path: '/analytics/categories', description: '지출 트렌드 분석' },
  { title: '월별 분석', path: '/analytics/monthly', description: '월별 재무 현황 분석' },
];

const utilityItems: MenuItem[] = [
  { title: '활동기록', path: '/activity-history', description: '공유가계부 활동 기록 및 복원' },
];


const Sidebar: FC<SidebarProps> = ({
  open,
  onClose,
  onMenuClick,
  activePath,
}) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const drawerWidth = 280;
  
  // 모바일에서는 temporary, 데스크톱에서는 permanent 사용
  const effectiveVariant = isMobile ? 'temporary' : 'permanent';
  
  const [scrollTop, setScrollTop] = React.useState(0);
  const scrollTimeoutRef = useRef<number | null>(null);

  // 스크롤 이벤트 핸들러 (최적화)
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  };
  
  // 컴포넌트 언마운트 시 timeout 클리어
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleMenuClick = (path: string) => {
    
    // onMenuClick prop이 있으면 MainPage로 이벤트 전달
    if (onMenuClick) {
      onMenuClick(path);
    } else {
      // 기존 방식 (React Router 사용)
      navigate(path);
    }
    
    if (isMobile) {
      onClose();
    }
  };

  // 메뉴 리스트를 렌더링하는 함수 (코드 중복 제거)
  // 활성 항목: 소프트 그린 필 + 좌측 액센트 바 — 현재 위치를 항상 보여주는 표준 내비 패턴
  const renderMenuItems = (items: MenuItem[], isSubmenu: boolean = false) => {
    return items.map((item) => {
      const isActive = item.path === activePath;
      return (
      <ListItem key={item.title} disablePadding sx={{ px: 2, mb: 1 }}>
        <ListItemButton
          onClick={() => handleMenuClick(item.path)}
          aria-current={isActive ? 'page' : undefined}
          sx={{
            position: 'relative',
            borderRadius: 'var(--r-16)',
            background: isActive ? 'var(--primary-bg)' : 'transparent',
            border: '1px solid',
            borderColor: isActive ? 'var(--primary-soft)' : 'transparent',
            transition: 'all var(--motion-fast)',
            pl: isSubmenu ? 3 : undefined,
            '&:hover': {
              background: isActive ? 'var(--primary-bg)' : 'var(--bg-muted)',
              borderColor: isActive ? 'var(--primary-soft)' : 'var(--border-subtle)',
              transform: isActive ? 'none' : 'translateX(2px)',
              boxShadow: isActive ? 'none' : 'var(--shadow-1)',
            },
            '&:active': {
              transform: 'translateX(1px) scale(0.99)',
            },
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? 'var(--primary-press)' : 'var(--fg-strong)',
                  }}
                >
                  {item.title}
                </Typography>
                {item.badge &&
                  (isNaN(Number(item.badge)) ? (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background:
                          item.badge === 'New' || item.badge === 'AI'
                            ? 'var(--primary)'
                            : item.badge === 'Hot'
                            ? 'var(--c-red-600)'
                            : 'var(--c-cool-700)',
                        color: 'var(--fg-on-primary)',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ) : (
                    <Badge badgeContent={item.badge} color="error" sx={{ mr: 1.5 }} />
                  ))}
              </Box>
            }
            secondary={
              <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)', fontWeight: 400 }}>
                {item.description}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
      );
    });
  };
  
  const drawerContent = (
    // 플로팅 카드 사이드바 — 캔버스 위에 라운드 카드로 떠 있는 형태 (Quixotic 레일 패턴)
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'transparent',
      p: 1.5,
      pt: '84px', // 헤더(72px) + 여백
    }}>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-24)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-1)',
        overflow: 'hidden',
      }}>

      {/* 헤더 */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-base)',
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center',
          background: 'var(--bg-elev-2)', borderRadius: 'var(--r-12)',
          p: 1.5, border: '1px solid var(--border-subtle)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--fg-strong)', lineHeight: 1.2, letterSpacing: '-0.012em' }}>
              사장님 가계부
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)', fontWeight: 500 }}>
              재무 관리의 시작
            </Typography>
          </Box>
        </Box>
        {isMobile && <Box sx={{ mt: 2 }}><AccountSwitcher /></Box>}
      </Box>

      {/* 메뉴 리스트 (스크롤 영역) */}
      <Box
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--c-cool-300)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--c-cool-700)',
          },
        }}
      >
        <List sx={{ py: 1 }} subheader={<li />}>
          {/* 스크롤 상단 그림자 효과 */}
          <Box
            sx={{
              position: 'sticky', top: 0, zIndex: 2,
              height: '1px',
              background: 'var(--border-subtle)',
              opacity: scrollTop > 0 ? 1 : 0,
              transition: 'opacity var(--motion-fast)',
            }}
          />

          <ListSubheader component="div" sx={{
            backgroundColor: 'var(--bg-base)',
            color: 'var(--fg-tertiary)',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.031em',
            textTransform: 'uppercase',
            lineHeight: '32px',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            pb: 0.5,
            mx: 1,
            mt: 0.5,
          }}>
            주요 기능
          </ListSubheader>
          {renderMenuItems(menuItems)}

          {/* 주요기능과 상세분석 사이 여백 추가 */}
          <Box sx={{ height: 16 }} />

          <Divider sx={{ my: 2, mx: 2, borderColor: 'var(--border-subtle)' }} />

          <ListSubheader component="div" sx={{
            backgroundColor: 'var(--bg-base)',
            color: 'var(--fg-tertiary)',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.031em',
            textTransform: 'uppercase',
            lineHeight: '40px',
            position: 'sticky',
            top: 40,
            zIndex: 1,
            pb: 1,
            mx: 1,
            mt: 1,
          }}>
            상세 분석
          </ListSubheader>
          {renderMenuItems(subMenuItems, true)}

          {/* 상세분석과 기타 사이 여백 추가 */}
          <Box sx={{ height: 16 }} />

          <Divider sx={{ my: 2, mx: 2, borderColor: 'var(--border-subtle)' }} />

          <ListSubheader component="div" sx={{
            backgroundColor: 'var(--bg-base)',
            color: 'var(--fg-tertiary)',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.031em',
            textTransform: 'uppercase',
            lineHeight: '40px',
            position: 'sticky',
            top: 80,
            zIndex: 1,
            pb: 1,
            mx: 1,
            mt: 1,
          }}>
            기타
          </ListSubheader>
          {renderMenuItems(utilityItems)}
        </List>
      </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={effectiveVariant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          background: 'transparent',
          overflow: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;